/**
 * Week and day calendar in the shape people already know from Google Calendar: a time axis, one
 * column per day, blocks placed and sized by their real start and duration, a line showing now.
 *
 * Everything on it can be moved with a pointer: drag a block to another time or day, drag its
 * bottom edge to make it longer, or drag an unscheduled task out of the tray and drop it on a slot.
 * Writes go straight to the store, and a linked task keeps its scheduled time in step.
 */
import { $, $$, byId, esc, on, toast } from "../lib/dom";
import { addDays, fmtShortDate, fmtTime, sameDay, startOfDay, startOfWeek } from "../lib/time";
import type { CalendarEvent, Task } from "../data/types";
import { createEvent, updateEvent, updateTask } from "../data/store";
import { state } from "../state";
import { openEventDetail } from "./events";

const PX_PER_HOUR = 52;
const SNAP_MIN = 15;
const MIN_MIN = 15;
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
/** Short by design: a gutter label has to fit the gutter. */
const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;

let weekOffset = 0;
let dayOffset: number | null = null; // set on narrow screens: a single day view
let rerenderRef: () => void = () => {};

const isPhone = () => window.innerWidth <= 760;
const minutesOf = (d: Date) => d.getHours() * 60 + d.getMinutes();
const snap = (min: number) => Math.round(min / SNAP_MIN) * SNAP_MIN;
const kindClass = (e: CalendarEvent) => `k-${e.kind}${e.flexible ? " is-flexible" : ""}`;

/** Lay overlapping blocks side by side, the way every calendar does. */
function pack(events: CalendarEvent[]) {
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime());
  const out: { e: CalendarEvent; col: number; cols: number }[] = [];
  let cluster: typeof out = [];
  let clusterEnd = 0;
  const flush = () => {
    const cols = cluster.reduce((m, c) => Math.max(m, c.col + 1), 0);
    cluster.forEach((c) => (c.cols = cols));
    out.push(...cluster);
    cluster = [];
    clusterEnd = 0;
  };
  for (const e of sorted) {
    if (cluster.length && e.start.getTime() >= clusterEnd) flush();
    const taken = new Set(cluster.filter((c) => c.e.end.getTime() > e.start.getTime()).map((c) => c.col));
    let col = 0;
    while (taken.has(col)) col++;
    cluster.push({ e, col, cols: col + 1 });
    clusterEnd = Math.max(clusterEnd, e.end.getTime());
  }
  if (cluster.length) flush();
  return out;
}

export function renderCalendar(events: CalendarEvent[], workStart = "09:00", workEnd = "17:30") {
  const host = byId("weekGrid");
  if (!host) return;

  const phone = isPhone();
  const monday = addDays(startOfWeek(new Date()), weekOffset * 7);
  const days = phone
    ? [addDays(startOfDay(new Date()), dayOffset ?? 0)]
    : Array.from({ length: 7 }, (_, i) => addDays(monday, i)).filter((d, i) => i < 5 || events.some((e) => sameDay(e.start, d)));
  const from = days[0]!, to = addDays(days[days.length - 1]!, 1);
  const shown = events.filter((e) => e.start >= from && e.start < to);

  // Hour range: working hours, widened to cover anything outside them
  const hm = (s: string) => Number(s.split(":")[0]);
  let first = hm(workStart), last = Math.max(hm(workEnd) + 1, hm(workStart) + 2);
  for (const e of shown) { first = Math.min(first, e.start.getHours()); last = Math.max(last, e.end.getHours() + (e.end.getMinutes() ? 1 : 0)); }
  first = Math.max(0, first - 1); last = Math.min(24, last + 1);
  const hours = last - first;
  const topOf = (d: Date) => ((minutesOf(d) - first * 60) / 60) * PX_PER_HOUR;

  const eyebrow = $("#page-calendar .eyebrow");
  if (eyebrow) eyebrow.textContent = phone
    ? `${fmtShortDate(days[0]!)}${(dayOffset ?? 0) === 0 ? " · today" : ""}`
    : `${fmtShortDate(days[0]!)} – ${fmtShortDate(days[days.length - 1]!)}${weekOffset === 0 ? " · this week" : weekOffset === 1 ? " · next week" : ""}`;
  const h2 = $("#page-calendar .week-card .section-head h2");
  if (h2) h2.textContent = phone
    ? ((dayOffset ?? 0) === 0 ? "Today" : (dayOffset ?? 0) === 1 ? "Tomorrow" : (dayOffset ?? 0) === -1 ? "Yesterday" : fmtShortDate(days[0]!))
    : weekOffset === 0 ? "This week" : weekOffset === 1 ? "Next week" : weekOffset === -1 ? "Last week" : `Week of ${fmtShortDate(days[0]!)}`;
  const progress = $("#page-calendar .progress");
  if (progress) {
    const meetings = shown.filter((e) => e.kind === "meeting").length, focus = shown.filter((e) => e.kind === "focus").length;
    progress.textContent = shown.length ? `${meetings} meeting${meetings === 1 ? "" : "s"} · ${focus} focus block${focus === 1 ? "" : "s"}` : "Nothing scheduled yet";
  }

  const today = new Date();
  const headCells = days.map((d) => `<div class="cal-day${sameDay(d, today) ? " is-today" : ""}"><span>${DAY_NAMES[(d.getDay() + 6) % 7]}</span><b>${d.getDate()}</b></div>`).join("");
  const gutter = Array.from({ length: hours }, (_, i) => `<div class="cal-hour" style="height:${PX_PER_HOUR}px"><span>${hourLabel(first + i)}</span></div>`).join("");

  const columns = days.map((d) => {
    const blocks = pack(shown.filter((e) => sameDay(e.start, d))).map(({ e, col, cols }) => {
      const top = Math.max(0, topOf(e.start));
      const height = Math.max(22, ((e.end.getTime() - e.start.getTime()) / 60000 / 60) * PX_PER_HOUR - 2);
      const width = 100 / cols;
      return `<article class="cal-event ${kindClass(e)}" data-event-id="${e.id}" tabindex="0" role="button"
        style="top:${top}px;height:${height}px;left:${col * width}%;width:calc(${width}% - 4px)"
        title="${esc(e.title)} · ${fmtTime(e.start)} to ${fmtTime(e.end)}">
        <b>${esc(e.title)}</b><small>${fmtTime(e.start)} – ${fmtTime(e.end)}</small>
        <span class="cal-resize" data-resize="${e.id}" aria-hidden="true"></span>
      </article>`;
    }).join("");
    const nowLine = sameDay(d, today) && minutesOf(today) >= first * 60 && minutesOf(today) <= last * 60
      ? `<div class="cal-now" style="top:${topOf(today)}px"><i></i></div>` : "";
    return `<div class="cal-col" data-day="${d.toISOString()}" style="height:${hours * PX_PER_HOUR}px">${nowLine}${blocks}</div>`;
  }).join("");

  host.innerHTML = `
    <div class="cal-head" style="grid-template-columns:var(--cal-gutter) repeat(${days.length},1fr)"><div></div>${headCells}</div>
    <div class="cal-body" id="calBody" style="grid-template-columns:var(--cal-gutter) repeat(${days.length},1fr)">
      <div class="cal-gutter">${gutter}</div>${columns}
    </div>`;

  wire(host, events, first, hours);
  renderTray();
}

/** Pointer handling: move a block, resize a block, or drop a task onto a slot. */
function wire(host: HTMLElement, events: CalendarEvent[], first: number, hours: number) {
  const body = byId("calBody")!;
  const colAt = (x: number, y: number) => {
    const cols = $$(".cal-col", body);
    return cols.find((c) => { const r = c.getBoundingClientRect(); return x >= r.left && x <= r.right && y >= r.top - 400 && y <= r.bottom + 400; }) ?? null;
  };
  const timeAt = (col: HTMLElement, clientY: number) => {
    const r = col.getBoundingClientRect();
    const mins = snap(first * 60 + ((clientY - r.top) / PX_PER_HOUR) * 60);
    const day = new Date(col.dataset.day!);
    const d = startOfDay(day);
    d.setMinutes(Math.max(0, Math.min(24 * 60 - MIN_MIN, mins)));
    return d;
  };

  $$(".cal-event", body).forEach((el) => {
    const id = el.dataset.eventId!;
    const ev = () => events.find((e) => e.id === id);

    on(el, "pointerdown", (raw) => {
      const e = raw as PointerEvent;
      if (e.button !== 0) return;
      const event = ev();
      if (!event) return;
      const resizing = (e.target as HTMLElement).hasAttribute("data-resize");
      const startY = e.clientY, startX = e.clientX;
      const durMin = (event.end.getTime() - event.start.getTime()) / 60000;
      const startMin = minutesOf(event.start), endMin = minutesOf(event.end);
      let moved = false, nextStart = event.start, nextEnd = event.end;

      // A mouse drags at once. A finger has to hold for a moment first, the way a phone home screen
      // works, so a scroll that happens to start on a meeting stays a scroll and a tap stays a tap.
      const touch = e.pointerType === "touch";
      let lifted = !touch, holdTimer = 0;
      const lift = () => {
        lifted = true;
        el.setPointerCapture(e.pointerId);
        el.classList.add(resizing ? "is-resizing" : "is-dragging", "is-lifted");
        try { navigator.vibrate?.(12); } catch { /* not every phone */ }
      };
      if (touch) holdTimer = window.setTimeout(lift, 240); else lift();

      // Work from how far the pointer has travelled, so dragging down one hour moves it one hour.
      const move = (m: PointerEvent) => {
        if (!lifted) {
          // Finger moved before the hold finished: this is a scroll, the browser takes it from here
          if (Math.abs(m.clientY - startY) > 8 || Math.abs(m.clientX - startX) > 8) window.clearTimeout(holdTimer);
          return;
        }
        if (!moved && Math.abs(m.clientY - startY) < 4 && Math.abs(m.clientX - startX) < 4) return;
        moved = true;
        const col = (colAt(m.clientX, m.clientY) ?? el.closest(".cal-col")) as HTMLElement;
        if (!col) return;
        const deltaMin = snap(((m.clientY - startY) / PX_PER_HOUR) * 60);
        const dayStart = startOfDay(new Date(col.dataset.day!));

        if (resizing) {
          const newEnd = Math.min(24 * 60, Math.max(startMin + MIN_MIN, snap(endMin + deltaMin)));
          nextEnd = new Date(startOfDay(event.start).getTime() + newEnd * 60000);
          el.style.height = `${Math.max(22, ((nextEnd.getTime() - nextStart.getTime()) / 60000 / 60) * PX_PER_HOUR - 2)}px`;
        } else {
          const newStart = Math.min(24 * 60 - durMin, Math.max(0, snap(startMin + deltaMin)));
          nextStart = new Date(dayStart.getTime() + newStart * 60000);
          nextEnd = new Date(nextStart.getTime() + durMin * 60000);
          el.style.top = `${((newStart - first * 60) / 60) * PX_PER_HOUR}px`;
          if (col !== el.parentElement) { col.appendChild(el); el.style.left = "0"; el.style.width = "calc(100% - 4px)"; }
        }
        el.querySelector("small")!.textContent = `${fmtTime(nextStart)} – ${fmtTime(nextEnd)}`;
      };

      const finish = () => {
        window.clearTimeout(holdTimer);
        if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
        el.removeEventListener("pointermove", move as EventListener);
        el.removeEventListener("pointerup", up as EventListener);
        el.removeEventListener("pointercancel", cancel as EventListener);
        el.classList.remove("is-dragging", "is-resizing", "is-lifted");
      };
      // The browser took the gesture (a scroll): put the block back where it was, open nothing
      const cancel = () => { finish(); if (moved) rerenderRef(); };
      const up = async () => {
        finish();
        if (!moved) { openEventDetail(event); return; }
        if (nextStart.getTime() === event.start.getTime() && nextEnd.getTime() === event.end.getTime()) { rerenderRef(); return; }
        try {
          await updateEvent(id, { start: nextStart, end: nextEnd });
          if (event.taskId) await updateTask(event.taskId, { scheduledStart: nextStart, scheduledEnd: nextEnd }, "user");
          toast(`${event.title} moved to ${fmtTime(nextStart)}`);
        } catch (err) {
          toast((err as Error).message || "Could not move that");
        }
        rerenderRef();
      };

      el.addEventListener("pointermove", move as EventListener);
      el.addEventListener("pointerup", up as EventListener);
      el.addEventListener("pointercancel", cancel as EventListener);
    });
    // Once lifted, the finger's movement is ours, not the page's. Before that the page may scroll.
    el.addEventListener("touchmove", (t) => { if (el.classList.contains("is-lifted")) t.preventDefault(); }, { passive: false });
    on(el, "contextmenu", (c) => c.preventDefault());

    on(el, "keydown", (raw) => {
      const k = raw as KeyboardEvent;
      const event = ev();
      if (!event) return;
      if (k.key === "Enter" || k.key === " ") { k.preventDefault(); openEventDetail(event); return; }
      const step = k.shiftKey ? 60 : SNAP_MIN;
      if (k.key !== "ArrowUp" && k.key !== "ArrowDown") return;
      k.preventDefault();
      const delta = (k.key === "ArrowUp" ? -step : step) * 60000;
      void (async () => {
        const s = new Date(event.start.getTime() + delta), e2 = new Date(event.end.getTime() + delta);
        await updateEvent(event.id, { start: s, end: e2 });
        if (event.taskId) await updateTask(event.taskId, { scheduledStart: s, scheduledEnd: e2 }, "user");
        rerenderRef();
      })();
    });
  });

  // Empty slot: click to create something there, or place the task picked in the tray
  $$(".cal-col", body).forEach((col) => {
    on(col, "click", (raw) => {
      const e = raw as MouseEvent;
      if ((e.target as HTMLElement).closest(".cal-event")) return;
      const at = timeAt(col, e.clientY);
      if (armedTask) { const id = armedTask; armTask(null); void scheduleTaskAt(id, at); return; }
      document.dispatchEvent(new CustomEvent("timo:create-at", { detail: { start: at.toISOString() } }));
    });
    // Dropping a task from the tray
    on(col, "dragover", (e) => { (e as DragEvent).preventDefault(); col.classList.add("is-target"); });
    on(col, "dragleave", () => col.classList.remove("is-target"));
    on(col, "drop", (raw) => {
      const e = raw as DragEvent;
      e.preventDefault();
      col.classList.remove("is-target");
      const taskId = e.dataTransfer?.getData("text/timo-task");
      if (!taskId) return;
      void scheduleTaskAt(taskId, timeAt(col, e.clientY));
    });
  });

  void hours;
  void host;
}

async function scheduleTaskAt(taskId: string, start: Date) {
  const task = state.tasks.find((t) => t.id === taskId);
  if (!task) return;
  const end = new Date(start.getTime() + (task.durationMin || 30) * 60000);
  try {
    const id = await createEvent({
      title: task.title, start, end, kind: "focus", flexible: true,
      source: "user", taskId: task.id, notes: task.notes ?? "",
    } as Omit<CalendarEvent, "id">);
    await updateTask(task.id, { scheduledStart: start, scheduledEnd: end, eventId: id ?? null, status: "open" }, "user");
    toast(`${task.title} scheduled for ${fmtTime(start)}`);
  } catch (err) {
    toast((err as Error).message || "Could not schedule that");
  }
  rerenderRef();
}

/** Tap a task in the tray, then tap a time: the phone way to schedule, and it works with a mouse too. */
let armedTask: string | null = null;
function armTask(id: string | null) {
  armedTask = id;
  $$(".cal-chip").forEach((c) => c.classList.toggle("is-armed", c.getAttribute("data-task") === id));
  byId("calBody")?.classList.toggle("is-placing", !!id);
  const hint = byId("calTrayHint");
  if (hint) hint.textContent = id ? "Now tap a time on the calendar" : "Drag onto a time, or tap one and then tap a time";
}
document.addEventListener("timo:escape", () => armTask(null));

/** Tasks with no time yet, ready to be dragged onto the grid. */
function renderTray() {
  const tray = byId("calTray");
  if (!tray) return;
  const waiting = state.tasks.filter((t: Task) => t.status === "open" && !t.scheduledStart).slice(0, 12);
  tray.innerHTML = waiting.length
    ? `<div class="cal-tray-head"><b>Not on the calendar yet</b><small id="calTrayHint">Drag onto a time, or tap one and then tap a time</small></div>
       <div class="cal-tray-list">${waiting.map((t) => `<button class="cal-chip" draggable="true" data-task="${t.id}"><b>${esc(t.title)}</b><small>${t.durationMin || 30} min</small></button>`).join("")}</div>`
    : `<div class="cal-tray-head"><b>Nothing waiting</b><small>Every task has a time</small></div>`;
  $$(".cal-chip", tray).forEach((chip) => {
    on(chip, "dragstart", (raw) => {
      const e = raw as DragEvent;
      e.dataTransfer?.setData("text/timo-task", chip.dataset.task!);
      e.dataTransfer!.effectAllowed = "move";
      chip.classList.add("is-dragging");
    });
    on(chip, "dragend", () => chip.classList.remove("is-dragging"));
    on(chip, "click", () => armTask(armedTask === chip.dataset.task ? null : chip.dataset.task!));
  });
  if (armedTask && !waiting.some((t) => t.id === armedTask)) armTask(null);
}

export function setupCalendar(rerender: () => void) {
  rerenderRef = rerender;
  const head = $("#page-calendar .date-head");
  if (!head || byId("weekPrev")) return;

  const controls = document.createElement("div");
  controls.className = "date-controls";
  controls.innerHTML = `<button class="btn" id="weekPrev" aria-label="Previous">‹</button><button class="btn" id="weekToday">Today</button><button class="btn" id="weekNext" aria-label="Next">›</button>`;
  head.querySelector(".open-create")?.before(controls);
  controls.appendChild(head.querySelector(".open-create")!);

  const step = (n: number) => { if (isPhone()) dayOffset = (dayOffset ?? 0) + n; else weekOffset += n; rerender(); };
  on(byId("weekPrev"), "click", () => step(-1));
  on(byId("weekNext"), "click", () => step(1));
  on(byId("weekToday"), "click", () => { weekOffset = 0; dayOffset = 0; rerender(); });

  // The tray of unscheduled tasks lives under the grid
  byId("weekGrid")?.closest(".week-card")?.insertAdjacentHTML("beforeend", `<div class="cal-tray" id="calTray"></div>`);

  let last = isPhone();
  window.addEventListener("resize", () => { const now = isPhone(); if (now !== last) { last = now; dayOffset = now ? 0 : null; rerender(); } });
}

/** Jump the calendar to the week containing a date (used after scheduling). */
export function showWeekOf(d: Date) {
  weekOffset = Math.round((startOfWeek(d).getTime() - startOfWeek(new Date()).getTime()) / (7 * 86_400_000));
  dayOffset = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86_400_000);
}
